<script setup lang="ts">
import {Menu, MenuButton, MenuItem, MenuItems} from '@headlessui/vue'
import NotificationBell from '~/layouts/partials/NotificationBell.vue'
import {ComputedRef} from 'vue'
import Place from '@/components/Icons/Place.vue'
import CookieConsentBanner from '@/components/CookieConsentBanner.vue';
import Footer from './_components/Footer.vue';
import SubjectsDropDown from './_components/SubjectsDropDown.vue'

import {useCookieConsent} from '@/composables/useCookieConsent';
import MobileNav from './_components/MobileNav.vue';
import Guide from '~/components/Guide.vue';
import Close from '~/components/PopUp/Close.vue';

const {removeConsent} = useCookieConsent();
const cookies = () => {
  removeConsent();
};
type NavigationLink = {
  text: string
  icon: string
  to: string
  duskSelector: string
  activeOnRoute: string[]
}

const [route, router, config, userStore, subjectStore, filterStore, errorStore] = [
  useRoute(),
  useRouter(),
  useRuntimeConfig(),
  useUserStore(),
  useSubjectStore(),
  useFilterStore(),
  useErrorStore(),
]
const {$_, $auth} = useNuxtApp()

const navigationLinks: ComputedRef<NavigationLink[]> = computed(() => [
  {
    text: $_('navigation.quick_overview'),
    icon: '<svg class="fill-inherit" xmlns="http://www.w3.org/2000/svg" width="20" height="20"><g clip-path="url(#a)"><path d="M6.69.114H2.394A2.385 2.385 0 0 0 0 2.489v4.265a2.385 2.385 0 0 0 2.393 2.375h4.298a2.385 2.385 0 0 0 2.393-2.375V2.489A2.385 2.385 0 0 0 6.691.114Zm.474 2.375v4.265c0 .258-.214.47-.473.47H2.393a.474.474 0 0 1-.473-.47V2.489c0-.258.214-.47.473-.47h4.298c.263 0 .473.212.473.47ZM17.576.114h-4.24a2.418 2.418 0 0 0-2.424 2.405v4.208a2.418 2.418 0 0 0 2.424 2.406h4.24A2.418 2.418 0 0 0 20 6.727V2.52A2.418 2.418 0 0 0 17.576.114Zm.504 2.405v4.208c0 .277-.225.5-.504.5h-4.24a.501.501 0 0 1-.504-.5V2.52c0-.276.225-.5.504-.5h4.24c.279 0 .504.224.504.5ZM6.69 10.947H2.394A2.385 2.385 0 0 0 0 13.322v4.265a2.385 2.385 0 0 0 2.393 2.375h4.298a2.385 2.385 0 0 0 2.393-2.375v-4.265a2.385 2.385 0 0 0-2.393-2.375Zm.474 2.375v4.265c0 .258-.214.47-.473.47H2.393a.474.474 0 0 1-.473-.47v-4.265c0-.257.214-.47.473-.47h4.298c.263 0 .473.209.473.47ZM17.576 10.947h-4.24a2.418 2.418 0 0 0-2.424 2.406v4.208a2.418 2.418 0 0 0 2.424 2.405h4.24A2.418 2.418 0 0 0 20 17.561v-4.212c0-1.326-1.088-2.402-2.424-2.402Zm.504 2.402v4.208c0 .277-.225.5-.504.5h-4.24a.501.501 0 0 1-.504-.5V13.35c0-.277.225-.5.504-.5h4.24a.506.506 0 0 1 .504.5Z"/></g><defs><clipPath id="a"><path d="M0 0h20v20H0z"/></clipPath></defs></svg>',
    to: '/dashboard',
    duskSelector: 'navbar-dashboard',
    activeOnRoute: ['/dashboard'],
  },
  {
    text: $_('navigation.my_waste'),
    icon: '<svg class="fill-inherit" xmlns="http://www.w3.org/2000/svg" width="20" height="20"><g clip-path="url(#a)"><path d="M7.065 10.27h1.812v6.1H7.065v-6.1ZM11.122 10.27h1.812v6.1h-1.812v-6.1Z"/><path d="M3.481 2.815H6.16v-.383C6.16 1.092 7.274 0 8.64 0h2.757c1.367 0 2.482 1.093 2.482 2.432v.383h2.639C17.885 2.815 19 3.907 19 5.247v3.247h-1.576v9.074c0 1.34-1.114 2.432-2.48 2.432H5.056c-1.367 0-2.482-1.093-2.482-2.432V8.494H1V5.247c0-1.34 1.115-2.432 2.481-2.432Zm1.576 15.409h9.886c.37 0 .67-.294.67-.656V8.494H4.387v9.074c0 .362.3.656.67.656Zm6.341-16.448H8.641c-.37 0-.67.294-.67.656v.383h4.097v-.383c0-.362-.3-.656-.67-.656Zm5.79 3.471c0-.363-.3-.656-.67-.656H3.482c-.37 0-.67.293-.67.656v1.471h14.377v-1.47Z"/></g><defs><clipPath id="a"><path d="M20 0H0v20h20z"/></clipPath></defs></svg>',
    to: '/my-waste',
    duskSelector: 'navbar-my-waste',
    activeOnRoute: ['/my-waste', '/my-unsorted-waste', '/my-waste/all'],
  },
  {
    text: $_('navigation.collection_yard'),
    icon: '<svg class="fill-inherit" xmlns="http://www.w3.org/2000/svg" width="20" height="20"><path d="M3.527 11.283c.211-.361.432-.737.647-1.107.202-.337.4-.674.606-1.016l.206-.347L7.31 10.34l.201-.159-1.154-5.439-5.571 1.31 2.666 1.756c-.038.063-.066.12-.099.174l-.915 1.54a635.053 635.053 0 0 0-2.009 3.393c-.544.925-.572 1.858-.075 2.773.507.939 1.305 1.415 2.37 1.425.409.005.822.005 1.23.005h1.891c.057 0 .108-.005.164-.01.024 0 .047-.005.07-.005l.132-.01v-1.848H4.874c-.685-.005-1.394-.005-2.093.005-.357.005-.658-.159-.812-.453a.967.967 0 0 1 .037-.962c.521-.838 1.028-1.709 1.52-2.551ZM19.61 12.958a35.546 35.546 0 0 0-.507-.842c-.225-.376-.455-.746-.69-1.127l-.366-.596-1.544.986.075.12c.146.236.287.472.428.703.342.564.661 1.098.994 1.637.212.347.226.679.043 1.01-.179.323-.446.473-.85.473h-.005c-1.285-.005-2.595-.005-3.862-.005h-1.84c-.037 0-.08 0-.127-.005v-3.09L7.446 16.27 11.148 20l.22-.115v-2.696h.189c.586 0 1.168 0 1.755.005 1.31.005 2.66.005 3.994-.01a2.68 2.68 0 0 0 2.332-1.42 2.813 2.813 0 0 0-.028-2.806ZM6.366 3.317l.005.072 1.436.934.131-.203.09-.14c.112-.182.225-.36.338-.543.225-.361.455-.732.685-1.097.249-.39.624-.544 1.037-.42.263.083.427.29.554.487.427.669.859 1.333 1.286 2.002.333.515.666 1.035 1 1.55.192.299.38.592.572.896l.192.303-2.374 1.67.225.106c.023.01.042.02.056.029.028.014.052.029.085.033.455.092.91.183 1.365.28 1.127.23 2.295.467 3.44.702.038.01.071.01.104.01a.266.266 0 0 0 .164-.048c.099-.067.122-.193.136-.274.113-.617.226-1.242.338-1.844.08-.438.164-.88.244-1.324l.23-1.29c.038-.207.07-.414.113-.63l.06-.337-2.684 1.858-.582-.91c-.262-.41-.53-.823-.784-1.227-.586-.92-1.187-1.868-1.806-2.783A2.637 2.637 0 0 0 9.849 0 2.595 2.595 0 0 0 7.67 1.112c-.324.457-.62.948-.906 1.425-.122.207-.253.419-.38.626a.174.174 0 0 0-.019.13c-.004.01 0 .014 0 .024Z"/></svg>',
    to: '/collection-yard',
    duskSelector: 'navbar-collection-yard',
    activeOnRoute: ['/collection-yard', '/collection-yard/all-visits'],
  },
  {
    text: $_('application/navigation.collection_point'),
    icon: '<svg class="fill-inherit" xmlns="http://www.w3.org/2000/svg" width="20" height="20"><path d="M10 0 0 7.488V20h7.783v-7.958h4.437V20H20V7.488L10 0ZM6.11 10.375v7.958H1.673V8.32L10 2.088l8.327 6.233v10.012H13.89v-7.958H6.11Z"/></svg>',
    to: '/collection-point',
    duskSelector: 'navbar-collection-point',
    activeOnRoute: ['/collection-point', '/collection-point/all-bins'],
  },
  {
    text: $_('navigation.tips'),
    icon: '<svg class="fill-inherit" xmlns="http://www.w3.org/2000/svg" width="22" height="22"><path d="M11 22a3.442 3.442 0 0 1-3.438-3.438v-2.835A5.856 5.856 0 0 1 5.156 11 5.85 5.85 0 0 1 11 5.156 5.85 5.85 0 0 1 16.844 11a5.856 5.856 0 0 1-2.407 4.727v2.835A3.442 3.442 0 0 1 11 22Zm-1.719-3.725v.288a1.718 1.718 0 1 0 3.438 0v-.288H9.28Zm0-1.72h3.438v-1.774l.412-.249A4.104 4.104 0 0 0 15.125 11 4.132 4.132 0 0 0 11 6.875 4.132 4.132 0 0 0 6.875 11c0 1.457.743 2.776 1.994 3.532l.412.25v1.774Zm10.815.688-2.977-1.718.86-1.487 2.977 1.719-.86 1.486Zm-18.192 0-.86-1.486 2.978-1.72.86 1.488-2.978 1.718ZM22 11.86h-3.438v-1.718H22v1.718Zm-18.563 0H0v-1.718h3.438v1.718Zm14.541-3.897-.86-1.487 2.979-1.718.859 1.486-2.978 1.72Zm-13.956 0L1.044 6.243l.86-1.486L4.88 6.475l-.86 1.487Zm11.503-3.08-1.487-.86 1.719-2.978 1.486.86-1.718 2.977Zm-9.05 0L4.757 1.903l1.486-.86 1.72 2.978-1.488.86Zm5.384-1.444h-1.718V0h1.718v3.438Z"/></svg>',
    to: '/tips',
    duskSelector: 'navbar-tips',
    activeOnRoute: ['/tips'],
  },
])

await userStore.fetchUser()
await userStore.fetchSubjects()
await subjectStore.fetchMotivationInfo(userStore.activeSubject?.id)
await filterStore.fetchReportingPeriod()

const user = computed(() => userStore.user)
const subjects = computed(() => userStore.subjects)

const globalErrors = computed(() => errorStore.globalErrors)

const isActive = (link: NavigationLink): boolean => link.activeOnRoute.includes(route.path)

const logout = async (): Promise<void> => {
  await $auth.logout()
}
const dropdowns = ref({
  subjects: false
})
const clearGlobalErrors = (): void => {
  errorStore.clearGlobalErrors()
}

useHead({
  titleTemplate: titleChunk => (titleChunk ? `${titleChunk} - JRK DIGITAL` : 'JRK DIGITAL'),
})

useHead({
  bodyAttrs: {
    class: 'antialiased',
  },
})
const isPWA = computed(() => window.matchMedia('(display-mode: standalone)').matches)
const showPaymentInfo = ref(true)

onMounted(() => {
  if (!isPWA.value && !localStorage.getItem('sawFeatures')) {
    featuresPopup.value = true
  }
})
const featuresPopup = ref(false)
watch(featuresPopup, (value) => {
  if (!value) {
    localStorage.setItem('sawFeatures', 'true')
  }
})

watch(
  () => [userStore.activeSubject?.id], () => {
    showPaymentInfo.value = userStore.activeSubject?.isPaymentInfoAvailable
  }, {immediate: true})

window.addEventListener('beforeinstallprompt', (e) => {
  console.log('beforeinstallprompt event fired');
  e.preventDefault();

  userStore.pwaInstallEvent = e
});
</script>

<template>
  <NuxtLoadingIndicator color="#569709"/>
  <NuxtPwaManifest/>

  <div>
    <header class="border-b-2 border-gray-300 bg-white py-2 xl:py-[14px]">
      <div dusk="navbar" class="flex items-center justify-between defaultSitePadding">
        <div class="flex items-center gap-x-6">
          <NuxtLink to="/dashboard">
            <img src="~/assets/img/logo.svg" alt="Econit logo"/>
          </NuxtLink>

          <nav class="hidden xl:block">
            <ul class="flex items-center gap-x-6">
              <li v-for="(link, index) in navigationLinks" :key="index" class="flex items-center">
                <NuxtLink :dusk="link.duskSelector" :to="link.to" :class="[
                  isActive(link)
                    ? 'rounded-bl-xl rounded-br-[4px] rounded-tl-[4px] rounded-tr-xl bg-green fill-white px-4 py-2 text-white hover:fill-white hover:text-white'
                    : 'text-gray-800 hover:fill-gray-600 hover:text-gray-600',
                  'inline-flex items-center gap-x-2 text-base font-bold transition',
                ]">
                  <span v-html="link.icon"></span>
                  <span v-html="link.text"></span>
                </NuxtLink>
              </li>
            </ul>
          </nav>
        </div>

        <div class="flex items-center gap-x-4 divide-x-2 divide-gray-300">
          <NotificationBell/>

          <div class="flex items-center gap-x-[12px] pl-4">
            <Menu v-slot="{ open }" as="div" class="relative inline-block text-left">
              <MenuButton dusk="navbar-menu"
                          class="inline-flex w-full items-center gap-x-[12px] fill-gray-800 text-base font-bold text-gray-800 transition hover:fill-green hover:text-green">
                <span
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-green text-xs uppercase text-white">
                  {{ user.initials }}
                </span>
                <div class="inline-flex items-center gap-x-2">
                  <span class="hidden xl:block">{{ user.name }}</span>
                  <svg :class="[open ? 'rotate-180' : '', 'transform transition-all']"
                       xmlns="http://www.w3.org/2000/svg" width="12" height="8">
                    <path d="M6 7.5 0 1.782 1.345.5 6 4.936 10.655.5 12 1.782 6 7.5Z"/>
                  </svg>
                </div>

              </MenuButton>
              <transition enter-active-class="transition duration-100 ease-out"
                          enter-from-class="transform scale-95 opacity-0"
                          enter-to-class="transform scale-100 opacity-100"
                          leave-active-class="transition duration-75 ease-in"
                          leave-from-class="transform scale-100 opacity-100"
                          leave-to-class="transform scale-95 opacity-0">
                <MenuItems
                  class="absolute right-0 z-20 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg focus:outline-none">
                  <div class="flex flex-col divide-y divide-gray-300 px-1 py-1">
                    <MenuItem v-slot="{ close }">
                      <button dusk="navbar-profile"
                              class="flex w-full items-center gap-x-3 py-3 pl-4 transition hover:bg-gray-100" @click="() => {
                        router.push({ path: '/profile' })
                        close()
                      }
                        ">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path fill-rule="evenodd" clip-rule="evenodd"
                                d="M12.0931 0C8.5025 0 5.58197 2.92053 5.58197 6.51109C5.58197 10.1016 8.5025 13.0222 12.0931 13.0222C15.6836 13.0222 18.6041 10.1016 18.6041 6.51109C18.6041 2.92053 15.6836 0 12.0931 0ZM7.71705 6.51109C7.71705 4.0985 9.68047 2.13508 12.0931 2.13508C14.5056 2.13508 16.4691 4.0985 16.4691 6.51109C16.4691 8.92368 14.5056 10.8871 12.0931 10.8871C9.68047 10.8871 7.71705 8.92368 7.71705 6.51109ZM10.7329 13.6089C8.26846 13.6089 5.94685 14.589 4.19853 16.3642C2.45919 18.1303 1.5 20.4638 1.5 22.9325C1.5 23.522 1.97797 24 2.56754 24H21.62C22.2095 24 22.6875 23.522 22.6875 22.9325C22.6875 20.4638 21.7283 18.1303 19.989 16.3642C18.2406 14.589 15.9191 13.6089 13.4546 13.6089H10.7329ZM20.4744 21.8649H3.71313C4.22397 18.4024 7.1812 15.744 10.7329 15.744H13.4546C17.0063 15.744 19.9635 18.4024 20.4744 21.8649Z"
                                fill="#808285"/>
                        </svg>

                        <span class="text-sm font-bold text-gray-800">{{ $_('navigation.my_information') }}</span>
                      </button>
                    </MenuItem>
                    <MenuItem v-slot="{ close }">
                      <button dusk="navbar-invoices"
                              class="flex w-full items-center gap-x-3 py-3 pl-4 transition hover:bg-gray-100"
                              @click.prevent="() => {
                        router.push({ path: '/profile/invoices' })
                        close()
                      }
                        ">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none">
                          <g fill="#808285" clip-path="url(#a)">
                            <path
                              d="M21.533.08a1.06 1.06 0 0 0-1.168.24l-2.28 2.313L15.8.32a1.066 1.066 0 0 0-1.52 0L12 2.633 9.72.32A1.092 1.092 0 0 0 8.96 0c-.286 0-.559.116-.76.32L5.915 2.633 3.634.32A1.062 1.062 0 0 0 2.467.08 1.102 1.102 0 0 0 1.8 1.09V22.91c0 .604.48 1.09 1.075 1.09h18.25c.595 0 1.075-.486 1.075-1.09V1.091c0-.444-.265-.837-.667-1.011ZM20.05 21.818H3.95V3.724l1.204 1.221a1.066 1.066 0 0 0 1.52 0l2.28-2.312 2.286 2.312c.423.43 1.104.43 1.52 0l2.279-2.312 2.28 2.312a1.066 1.066 0 0 0 1.52 0l1.203-1.221v18.094h.007Z"/>
                            <path
                              d="M16.824 16.727H7.176c-.595 0-1.075.487-1.075 1.091s.48 1.09 1.075 1.09h9.648c.595 0 1.075-.486 1.075-1.09 0-.604-.487-1.09-1.075-1.09ZM7.176 14.545h5.347c.595 0 1.075-.487 1.075-1.09 0-.604-.48-1.091-1.075-1.091H7.176c-.595 0-1.075.487-1.075 1.09 0 .604.48 1.091 1.075 1.091ZM7.176 10.182h5.347c.595 0 1.075-.488 1.075-1.091 0-.604-.48-1.091-1.075-1.091H7.176C6.58 8 6.1 8.487 6.1 9.09c0 .604.48 1.092 1.075 1.092Z"/>
                          </g>
                          <defs>
                            <clipPath id="a">
                              <path fill="#fff" d="M0 0h24v24H0z"/>
                            </clipPath>
                          </defs>
                        </svg>

                        <span class="text-sm font-bold text-gray-800">{{ $_('navigation.invoices') }}</span>
                      </button>
                    </MenuItem>
                    <MenuItem v-if="config.public.APP_ENV !== 'demo'">
                      <button dusk="navbar-logout"
                              class="flex w-full items-center gap-x-3 py-3 pl-4 transition hover:bg-gray-100"
                              @click="logout">
                        <div class="block">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none">
                            <g fill="#808285" clip-path="url(#a)">
                              <path
                                d="M17.863 6.138A1.09 1.09 0 0 0 16.32 7.68l3.229 3.23H6.183a1.091 1.091 0 0 0 0 2.18h13.366l-3.229 3.23a1.09 1.09 0 1 0 1.543 1.542l5.09-5.09a1.09 1.09 0 0 0 0-1.543l-5.09-5.091Z"/>
                              <path
                                d="M11.273 24a1.09 1.09 0 1 0 0-2.182H2.909V2.182h8.364a1.09 1.09 0 0 0 0-2.182H1.818a1.09 1.09 0 0 0-1.09 1.09v21.82c0 .602.488 1.09 1.09 1.09h9.455Z"/>
                            </g>
                            <defs>
                              <clipPath id="a">
                                <path fill="#fff" d="M0 0h24v24H0z"/>
                              </clipPath>
                            </defs>
                          </svg>
                        </div>

                        <span class="text-sm font-bold text-gray-800">{{ $_('navigation.logout') }}</span>
                      </button>
                    </MenuItem>
                  </div>
                </MenuItems>
              </transition>
            </Menu>
            <button class="yardsWrap">
              <Place :default-fill="false" icon-class="fill-green" @click="dropdowns.subjects = !dropdowns.subjects" />
              <SubjectsDropDown v-if="dropdowns.subjects" v-model="dropdowns.subjects" />
            </button>

          </div>
        </div>
      </div>
    </header>

    <div v-if="config.public.APP_ENV === 'demo'" class="w-full bg-green-light px-4 py-2 xl:px-0">
      <div class="container flex items-center">
        <div class="inline-flex items-center gap-x-2">
          <div>
            <svg class="h-6 w-6 text-green" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor"
                 stroke-width="1.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round"
                    d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15M12 9l-3 3m0 0 3 3m-3-3h12.75"/>
            </svg>
          </div>

          <p class="text-sm text-green">
            {{ $_('demo.alert.text') }}
            <NuxtLink :to="config.public.MAIN_APP_URL" class="underline hover:no-underline">
              {{ $_('demo.alert.logout') }}
            </NuxtLink>
          </p>
        </div>
      </div>
    </div>

    <!-- <div v-else class="w-full bg-red-light px-4 py-2 xl:px-0">
      <div class="container flex items-center">
        <div class="inline-flex items-center gap-x-2">
          <div>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" fill="none">
              <g fill="#D14A4A" clip-path="url(#a)">
                <path
                  d="m19.698 14.383-7.91-12.891A2.114 2.114 0 0 0 10 .5c-.727 0-1.413.381-1.79.992L.302 14.384a2.027 2.027 0 0 0-.03 2.069 2.096 2.096 0 0 0 1.82 1.043h15.818c.754 0 1.45-.4 1.82-1.043a2.01 2.01 0 0 0-.03-2.07Zm-1.683 1.118a.12.12 0 0 1-.106.06H2.09a.125.125 0 0 1-.105-.06.12.12 0 0 1 0-.118L9.895 2.49a.124.124 0 0 1 .101-.055c.041 0 .08.022.102.055l7.91 12.892a.113.113 0 0 1 .007.118Z"
                />
                <path
                  d="M8.799 6.625c0 .693.041 1.351.083 1.988.041.625.079 1.273.079 1.943 0 .518.41.855 1.043.855.501 0 1.032-.3 1.032-.855 0-.67.042-1.318.08-1.943.04-.637.082-1.295.082-1.988 0-.607-.505-1.05-1.198-1.05-.704 0-1.201.432-1.201 1.05ZM10.018 12.248c-.704 0-1.254.54-1.254 1.233 0 .68.56 1.232 1.254 1.232.674 0 1.243-.566 1.243-1.232 0-.667-.569-1.233-1.243-1.233Z"
                />
              </g>
              <defs>
                <clipPath id="a"><path fill="#fff" d="M0 .5h20v17H0z" /></clipPath>
              </defs>
            </svg>
          </div>

          <p class="text-sm text-red">
            Vaše faktura FA456452 je po splatnosti. Uhraďte ji prosím.
            <button
              class="underline hover:no-underline"
              @click.prevent="() => router.push({ path: '/profile/invoices' })"
            >
              Zobrazit přehled faktur.
            </button>
          </p>
        </div>
      </div>
    </div> -->
    <div v-if="route.path.includes('/profile')" class="bg-white container-x-padding border-b-2">
      <ul class="flex justify-around md:justify-normal space-x-4 md:space-x-8 text-sm">
        <li :class="{'font-semibold': route.fullPath === '/profile/invoices'}" class="relative py-2">
          <nuxt-link to="/profile/invoices">{{ $_('profile.tabs.invoices') }}</nuxt-link>
          <div v-if="route.fullPath === '/profile/invoices'"
               class="absolute bottom-0 translate-y-0.5 left-0 h-0.5 w-full bg-green"/>
        </li>
        <li :class="{'font-bold': route.fullPath === '/profile/notifications'}" class="relative py-2">
          <nuxt-link to="/profile/notifications">{{ $_('profile.tabs.notifications') }}</nuxt-link>
          <div v-if="route.fullPath === '/profile/notifications'"
               class="absolute bottom-0 translate-y-0.5 left-0 h-0.5 w-full bg-green"/>
        </li>
        <li :class="{'font-bold': route.fullPath === '/profile'}" class="relative py-2">
          <nuxt-link to="/profile">{{ $_('profile.tabs.my_details') }}</nuxt-link>
          <div v-if="route.fullPath === '/profile'"
               class="absolute bottom-0 translate-y-0.5 left-0 h-0.5 w-full bg-green"/>
        </li>
      </ul>
    </div>
    <main :key="userStore.activeSubject?.id" class="bg-gray-100">
      <div v-if="globalErrors.length > 0" class="mb-4 rounded-md bg-red-light p-4" dusk="alert-danger">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                    clip-rule="evenodd"/>
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red">{{ $_('errors.global.title') }}</h3>
            <div class="mt-2 text-sm text-red">
              <ul role="list" class="list-disc space-y-1 pl-5">
                <li v-for="(error, index) in globalErrors" :key="index">{{ error.message }}</li>
              </ul>
            </div>
          </div>

          <div class="ml-auto pl-3">
            <div class="-mx-1.5 -my-1.5">
              <button type="button" class="inline-flex rounded-md p-1.5 text-red focus:outline-none"
                      @click="clearGlobalErrors">
                <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      <slot/>
    </main>

    <Footer></Footer>

    <MobileNav :navigation-links="navigationLinks"/>
  </div>
  <div class="fixedClass">
    <CookieConsentBanner/>
  </div>
  <PopUp v-model="featuresPopup">
    <Guide class="mb-6" v-model="featuresPopup"></Guide>
    <Close v-model="featuresPopup"></Close>
  </PopUp>
</template>
<style scoped>
main {
  padding: 24px var(--default-whitespace);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.container-x-padding {
  padding-left: var(--default-whitespace);
  padding-right: var(--default-whitespace);
}

.fixedClass {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 50;
}

.yardsWrap {
  position: relative
}
</style>
